/**
 * Derive the `(channel, aggregate type)` pairs a baseline should fit, from the charts currently on
 * the Performance page.
 *
 * WHY PAIRS AND NOT A CHANNEL LIST
 * Two series on one page can plot `Bsp_kph [AVG]` and `Bsp_kph [STD]`, and each needs its own
 * curve. Alinghi sends a flat `loess_channels` list, which on its own only ever produces AVG
 * curves -- its non-AVG buckets exist purely as a side effect of the contour `object_specs`
 * carrying `x_agr_type`/`y_agr_type`. Since RaceSight has no contours, sending pairs directly is
 * both clearer and the only way to keep STD/AAV curves at all.
 *
 * WHY DERIVED FROM THE CHARTS
 * Fitting every column of `events_aggregate` would make saves slow and the payload large. The
 * charts on screen are what the user is comparing against, so those are what get fitted -- plus a
 * small fixed core so a baseline is never useless. A chart built later on a channel this baseline
 * never fitted simply shows no curve until the baseline is saved again.
 */

import { defaultChannelsStore } from '../store/defaultChannelsStore';

/** Wire shape: snake_case to match the rest of the API. */
export interface BaselineChannelPair {
  channel: string;
  agr_type: string;
}

/** Aggregate types the Performance builder offers. Anything else collapses to AVG. */
const SUPPORTED_AGR_TYPES = new Set(['AVG', 'STD', 'AAV']);

/** Placeholders the chart config uses for "inherit the page's x-axis". */
const PLACEHOLDER_AXES = new Set(['PAGE DEFAULT', 'DEFAULT', '']);

function normalizeAgr(agrType: unknown): string {
  const t = String(agrType || 'AVG').trim().toUpperCase();
  return SUPPORTED_AGR_TYPES.has(t) ? t : 'AVG';
}

/** A channel reference from the chart config, or '' when it is a placeholder. */
function resolveChannel(ref: unknown, fallback?: string): string {
  if (typeof ref === 'string') {
    const name = ref.trim();
    return PLACEHOLDER_AXES.has(name.toUpperCase()) ? (fallback || '') : name;
  }
  const obj = ref as { dataField?: string; name?: string } | null | undefined;
  const name = String(obj?.dataField || obj?.name || '').trim();
  return PLACEHOLDER_AXES.has(name.toUpperCase()) ? (fallback || '') : name;
}

/**
 * The channels a baseline always fits, at AVG.
 *
 * TWS is the fit's x and TWA decides the leg, so neither is optional. BSP / VMG / VMG% are included
 * because they are the channels anyone looks at first — without them a baseline saved from a page
 * of unusual charts would be technically valid and practically useless.
 */
export function coreBaselineChannels(): string[] {
  const { twsName, twaName, bspName, vmgName, vmgPercName } = defaultChannelsStore;
  return [twsName(), twaName(), bspName(), vmgName(), vmgPercName()]
    .map((c) => String(c || '').trim())
    .filter((c) => c !== '');
}

/**
 * Walk the page's chart groups and collect every `(channel, aggregate)` pair they display.
 *
 * `groups` is the output of the page's own `chartGroups()`, which has already resolved
 * `PAGE DEFAULT` x-axes and uppercased the types. `pageXAxis` is the fallback for anything that
 * slipped through.
 */
export function buildBaselineChannelPairs(
  groups: unknown[],
  pageXAxis?: string
): BaselineChannelPair[] {
  const pairs = new Map<string, BaselineChannelPair>();
  const add = (channel: string, agrType: unknown) => {
    const name = String(channel || '').trim();
    if (name === '') { return; }
    const agr = normalizeAgr(agrType);
    pairs.set(`${name}|${agr}`, { channel: name, agr_type: agr });
  };

  // The core set first, so it is present even when the page has no charts configured yet.
  for (const channel of coreBaselineChannels()) { add(channel, 'AVG'); }

  for (const group of (groups || []) as any[]) {
    const series = group?.charts?.[0]?.series;
    if (!Array.isArray(series)) { continue; }
    for (const s of series) {
      const xChannel = String(s?.xaxisValue || '').trim() || resolveChannel(s?.xaxis, pageXAxis);
      const yChannel = resolveChannel(s?.yaxis);
      add(xChannel, s?.xType);
      add(yChannel, s?.yType);
    }
  }

  return [...pairs.values()];
}
